import { Component, inject } from "@angular/core";
import { CommonModule } from "@angular/common";
import { Router } from "@angular/router";
import { AuthService } from '@services/auth.service';
import { AnalyticsService } from "../../../../core/service/analytics.service";
import { ViewportScroller } from "@angular/common";
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { RegisterModal } from "../register-modal/register-modal";
import { LoginComponent } from '@auth/pages/login.component';
import { WorkTypeSelectionModalComponent, ConstructionType, WorkTypeModalConfig } from '../../../page/quote/modal/work-type-selection-modal.component';
import { NAVIGATION_ROUTES } from '@shared/constants';
import { SvgIconComponent } from '@components/svg-icon/svg-icon.component';
import { SvgIcon } from '@services/svg-icon.service';
import { MenuButtonComponent, MenuButtonData } from '@molecules/menu-button/menu-button.component';
import { openTrainingUrl } from '@utils/open-training-url';
@Component({
  selector: "app-navigation",
  templateUrl: './navigation.component.html',
  standalone: true,
  imports: [CommonModule, SvgIconComponent, MenuButtonComponent]
})
export class NavigationComponent {
  readonly SvgIcon = SvgIcon;
  isMenuOpen = false;
  private bottomSheet = inject(MatBottomSheet);
  private auth = inject(AuthService);
  // Menu buttons data
  readonly menuButtons: MenuButtonData[] = [
    {
      iconSrc: 'assets/icons/calcular-materiales.png',
      title: 'Calcular Materiales',
      showPill: true,
      pillText: 'Nuevo'
    },
    {
      iconSrc: 'assets/icons/cotizar-materiales.png',
      title: '¡Arma tu Lista de Materiales!'
    },
    {
      iconSrc: 'assets/icons/productos-unicon.png',
      title: 'Pedir productos UNICON'
    },
    {
      iconSrc: 'assets/icons/crear-contrato.png',
      title: 'Crear contrato'
    },
  ];
  
  constructor(private router: Router, private analytics: AnalyticsService, private viewportScroller: ViewportScroller) {}

  goToHome() {
    this.analytics.trackButtonClick('Logo_Cantera', 'navigation');
    this.router.navigate([NAVIGATION_ROUTES.ROOT]);
  }

  goToLogin() {
    this.analytics.trackButtonClick('Ingresar', 'navigation');
    this.bottomSheet.open(LoginComponent, {
      data: {
        source: 'navigation',
        returnUrl: NAVIGATION_ROUTES.INICIO,
      },
      disableClose: false,
      hasBackdrop: true,
      panelClass: 'login-bottom-sheet',
    });
  }

  goToRegister() {
    this.analytics.trackButtonClick('Registrarse', 'navigation_menu');
    this.closeMenu();
    this.bottomSheet.open(RegisterModal, {
      disableClose: false,
      hasBackdrop: true,
      panelClass: 'register-modal-bottom-sheet',
    });
  }

  goToOwner() {
    this.analytics.trackButtonClick('Soy_Propietario', 'navigation_menu');
    this.closeMenu();
    this.router.navigate(['/owner']);
  }

  toggleMenu() {
    if (this.isMenuOpen) {
      this.closeMenu();
    } else {
      this.openMenu();
    }
  }

  private openMenu() {
    this.isMenuOpen = true;
    // Prevenir scroll del body cuando el menú está abierto
    document.body.style.overflow = 'hidden';
  }

  closeMenu() {
    this.isMenuOpen = false;
    // Restaurar scroll del body cuando el menú se cierra
    document.body.style.overflow = '';
  }

  // Método para prevenir la propagación del scroll en el contenido del menú
  onMenuScroll(event: Event) {
    event.stopPropagation();
  }

  openRegisterModal() {
    this.bottomSheet.open(RegisterModal, {
      disableClose: false,
      hasBackdrop: true,
      panelClass: 'register-modal-bottom-sheet',
    });
  }

  goToLearnSection() {
    this.analytics.trackButtonClick('Capacitarme', 'navigation_menu');
    this.closeMenu(); // Usar método mejorado para cerrar el menú
    setTimeout(() => {
      this.scrollToLearnSection();
    }, 300); // Delay para permitir que la animación de cierre termine
  }

  private scrollToLearnSection(): void {
    // Buscar el elemento de la sección de aprender
    const learnElement = document.querySelector('#aprende') || document.querySelector('app-learn-section');
    
    if (learnElement) {
      learnElement.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    } else {
      // Fallback usando ViewportScroller si no encuentra el elemento
      this.viewportScroller.scrollToAnchor('aprende');
    }
  }

  openFlowToCreateContract(){
    this.router.navigate([NAVIGATION_ROUTES.CONTRACT_NEW]);
  }

  openWorkTypeSelector(): void {
    this.analytics.trackButtonClick('Cotizar_Materiales', 'navigation_menu');
    this.closeMenu();
    
    const bottomSheetRef = this.bottomSheet.open(WorkTypeSelectionModalComponent, {
      disableClose: false,
      hasBackdrop: true,
      panelClass: 'custom-bottom-sheet-quote',
    });

    // Escuchar cuando se cierra el bottom sheet con resultado
    bottomSheetRef.afterDismissed().subscribe((types?: ConstructionType[]) => {
      if (types && types.length > 0) {
        this.onWorkTypeSelected(types);
      }
    });
  }

  private onWorkTypeSelected(constructionTypes: ConstructionType[]): void {
    console.log('Tipos de trabajo seleccionados desde navegación:', constructionTypes);
    // Aquí puedes agregar lógica adicional si es necesaria
  }

  private openTrainingUrl(): void {
    openTrainingUrl(this.auth.constructorRedirectUrl());
  }

  private openCalculatorTypeSelector(): void {
    const config: WorkTypeModalConfig = {
      title: '¿Qué vas a calcular?',
      subtitle: '',
      targetRoute: NAVIGATION_ROUTES.CALCULATOR,
      mode: 'calculator'
    };

    this.bottomSheet.open(WorkTypeSelectionModalComponent, {
      disableClose: false,
      hasBackdrop: true,
      panelClass: 'custom-bottom-sheet-quote',
      data: { config }
    });
  }

  onMenuButtonClick(index: number): void {
    this.closeMenu();
    
    switch (index) {
      case 0: // Calcular Materiales
        this.analytics.trackButtonClick('Calcular_Materiales', 'navigation_menu');
        this.openCalculatorTypeSelector();
        break;
      case 1: // Cotizar materiales
        this.analytics.trackButtonClick('Cotizar_Materiales', 'navigation_menu');
        this.openWorkTypeSelector();
        break;
      case 2: // Pedir productos UNICON
        this.analytics.trackButtonClick('Pedir_Productos_UNICON', 'navigation_menu');
        this.openRegisterModal();
        break;
      case 3: // Crear contrato
        this.analytics.trackButtonClick('Crear_Contrato', 'navigation_menu');
        this.openFlowToCreateContract();
        break;
      case 4: // Capacitarme
        this.analytics.trackButtonClick('Capacitarme', 'navigation_menu');
        this.openTrainingUrl();
        break;
    }
  }
}
